import React, { useState, useEffect, useRef } from "react";
import {
  GoogleMap,
  Marker,
  useJsApiLoader,
  InfoWindow,
} from "@react-google-maps/api";

import "../../../Map/map.css";

import MapStyles from "../../../Map/mapStyles";

import { useSelector } from "react-redux";
import ReplayIcon from "@mui/icons-material/Replay";
import { Tooltip } from "antd";
import Events from "../../../../Analytics/Events";
import Loader from "../../../../Helper/Loaders/Loader";
import { ThemeProperties } from "../../../../Theme/ThemeProperties";
import { Box, Grid2, Typography } from "@mui/material";
import CloseButton from "../../../../Components/CloseButton";
import { GOOGLE_API_KEY } from "../../../../Static/Constants/GoogleConfig";
import { useTranslation } from "react-i18next";

function BatteryDeviceInfoMap({batteryGeoLocation, markerIcon}) {
  const [map, setMap] = useState(null);
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const {t} = useTranslation(['common', 'digital_twin']);

  const currentZoom = 15;
  const mapClusterClick = useSelector((state) => state.MapClusterClick.value);

  const ref = useRef();
  const resetCluster = useRef();

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_API_KEY,
  });

  useEffect(() => {
    resetMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batteryGeoLocation]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onLoad = React.useCallback(function callback(map) {
    var mybounds = new window.google.maps.LatLngBounds();
    if (batteryGeoLocation) {
      mybounds.extend(new window.google.maps.LatLng(batteryGeoLocation.latitude, batteryGeoLocation.longitude));
    }

    map.fitBounds(mybounds);

    // set zoom
    const zoomListener = map.addListener("bounds_changed", () => {
      map.setZoom(currentZoom);
      window.google.maps.event.removeListener(zoomListener);
    });

    setMap(map);

    if (map && ref) {
      map.controls[window.google.maps.ControlPosition["TOP_RIGHT"]].push(
        ref.current
      );
    }
    if (map && resetCluster) {
      map.controls[window.google.maps.ControlPosition["TOP_LEFT"]].push(
        resetCluster.current
      );
    }
  });

  function resetMap() {
    if (map !== null) {
      var mybounds = new window.google.maps.LatLngBounds();
      mybounds.extend(new window.google.maps.LatLng(batteryGeoLocation.latitude, batteryGeoLocation.longitude));
      map.fitBounds(mybounds);

      // set zoom
      const zoomListener = map.addListener("bounds_changed", () => {
        map.setZoom(currentZoom);
        window.google.maps.event.removeListener(zoomListener);
      });

      setMap(map);
    }
  }

  const onUnmount = React.useCallback(function callback(map) {
    setMap(null);
  }, []);

  const handleToggleClose = () => {
    setShowInfoWindow(false);
  };

  const containerHeight = '465px';

  return isLoaded ? (
    <Box sx={{ height: containerHeight }}>
      <Box className={"notMainDiv"}>
        <Box className="mapDiv">
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%", position: "relative" }}
            center={{ lat: batteryGeoLocation.latitude, lng: batteryGeoLocation.longitude }}
            mapContainerClassName="mapContainer"
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={{
              fullscreenControl: false,
              styles: MapStyles,
              minZoom: 2,
              maxZoom: 22,
            }}
          >
            <Marker
              position={{
                lat: batteryGeoLocation["latitude"],
                lng: batteryGeoLocation["longitude"],
              }}
              onClick={() => {
                Events("clicked Map InfoWindow");
                setShowInfoWindow(true);
              }}
              icon={markerIcon}
            >
              {showInfoWindow && (
                <InfoWindow
                  position={{
                    lat: batteryGeoLocation["latitude"],
                    lng: batteryGeoLocation["longitude"],
                  }}
                >
                  <Box sx={{ height: "100%", width: "196px", padding: 1.5 }}>
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 0.5}}>
                      <CloseButton onClick={handleToggleClose} size="small" />
                    </Box>
                    <Grid2 sx={{gap: 0.5}}>
                      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <Typography variant="fw400s12c686868">{t('digital_twin:location', 'Location')}:</Typography>
                        <Typography variant="fw400s12c000000">{batteryGeoLocation.location}</Typography>
                      </Box>
                      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <Typography variant="fw400s12c686868">{t('digital_twin:lat/long', 'Lat/Long')}:</Typography>
                        <Typography variant="fw400s12c000000">{`${batteryGeoLocation.latitude}/${batteryGeoLocation.longitude}`}</Typography>
                      </Box>
                    </Grid2>
                  </Box>
                </InfoWindow>
              )}
            </Marker>
            <Box sx={{ padding: "1rem" }}>
              <Tooltip
                title="Reset the map"
                placement={"bottom"}
                zIndex={10001}
              >
                <ReplayIcon
                  ref={resetCluster}
                  onClick={() => {
                    resetMap();
                    setShowInfoWindow(false);
                  }}
                  sx={{
                    fontSize: "20px",
                    cursor: mapClusterClick ? "pointer" : "default",
                    left: { xs: 225, sm: 225, md: 225, lg: 225, xl: 245 },
                    border: `1px solid ${
                      mapClusterClick
                        ? ThemeProperties.purple
                        : ThemeProperties.dark_grey
                    }`,
                    backgroundColor: mapClusterClick
                      ? ThemeProperties.purple
                      : "white",
                    padding: "5px",
                    borderRadius: "50%",
                    margin: "14px 5px 5px",
                    color: mapClusterClick
                      ? ThemeProperties.white
                      : ThemeProperties.dark_grey,
                    opacity: mapClusterClick ? 1 : 0.8,
                  }}
                ></ReplayIcon>
              </Tooltip>
            </Box>
          </GoogleMap>
        </Box>
      </Box>
    </Box>
  ) : (
    <Box style={{ height: containerHeight }}>
      <Loader />
    </Box>
  );
}

export default React.memo(BatteryDeviceInfoMap);
